import { useState, useEffect } from 'react';
import axios from 'axios';
import { Warehouse, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const OperatoreCellaIn = () => {
  const [fornitori, setFornitori] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    prodotto_sigla: '',
    fornitore: '',
    kg: '',
    data_ingresso: getItalianDateTime()
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [fornitoriRes, prodottiRes] = await Promise.all([
          axios.get(`${API}/fornitori`).catch(e => ({ data: [] })),
          axios.get(`${API}/prodotti`).catch(e => ({ data: [] }))
        ]);
        setFornitori(fornitoriRes.data || []);
        setProdotti(prodottiRes.data || []);
      } catch (error) {
        console.error('Errore caricamento dati:', error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Trova il prodotto selezionato per ottenere il nome completo
    const prodottoSelezionato = prodotti.find(p => p.sigla === formData.prodotto_sigla);
    
    try {
      // REGISTRA DIRETTAMENTE IL PRODOTTO IN CELLA (senza approvazione)
      await axios.post(`${API}/cella/bins`, {
        prodotto_sigla: formData.prodotto_sigla,
        prodotto_nome: prodottoSelezionato?.nome || formData.prodotto_sigla,
        fornitore: formData.fornitore,
        kg: parseFloat(formData.kg),
        data_ingresso: new Date(formData.data_ingresso).toISOString(),
        note: 'Inserito da operatore'
      });
      
      setSuccess(true);
      setFormData({ 
        prodotto_sigla: '', 
        fornitore: '', 
        kg: '',
        data_ingresso: getItalianDateTime()
      });
      
      setTimeout(() => setSuccess(false), 3000);
      toast.success('Prodotto registrato in cella!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Prodotto Registrato!</h2>
          <p className="text-white/60">Il prodotto è stato aggiunto in cella</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-4" data-testid="operatore-cella-in-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pt-8">
          <div className="w-16 h-16 bg-[#8B5CF6]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Warehouse className="w-8 h-8 text-[#8B5CF6]" />
          </div>
          <h1 className="text-2xl font-bold text-white">CELLA IN</h1>
          <p className="text-white/50">Registra ingresso in cella</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Prodotto *</label>
              <select
                value={formData.prodotto_sigla}
                onChange={(e) => setFormData({ ...formData, prodotto_sigla: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white"
                required
                data-testid="cella-in-prodotto-select"
              >
                <option value="" className="bg-[#0F172A]">Seleziona prodotto...</option>
                {prodotti.map((p) => (
                  <option key={p.sigla} value={p.sigla} className="bg-[#0F172A]">
                    {p.sigla} - {p.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Fornitore *</label>
              <select
                value={formData.fornitore}
                onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white"
                required
                data-testid="cella-in-fornitore-select"
              >
                <option value="" className="bg-[#0F172A]">Seleziona fornitore...</option>
                {fornitori.map((f) => (
                  <option key={f.nome} value={f.nome} className="bg-[#0F172A]">{f.nome}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Quantità (KG) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.kg}
                onChange={(e) => setFormData({ ...formData, kg: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-2xl font-bold text-center"
                placeholder="0.00"
                required
                data-testid="cella-in-kg-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Data/Ora Ingresso</label>
              <input
                type="datetime-local"
                value={formData.data_ingresso}
                onChange={(e) => setFormData({ ...formData, data_ingresso: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#8B5CF6] text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-[#8B5CF6]/80 transition-colors disabled:opacity-50"
            data-testid="cella-in-submit-btn"
          >
            {loading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Plus className="w-6 h-6" />
                REGISTRA INGRESSO
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OperatoreCellaIn;
